import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import SfhSecondaryButton from './SfhSecondaryButton';
import SectionLoader from '../../Loader/SectionLoader';
import ProductImage from '../../LegacyOrder/ProductImage';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import SellFromHomeStatusTimeline from './SellFromHomeStatusTimeline';
import SellFromHomeCancelOrderDrawer from './SellFromHomeCancelOrderDrawer';
import SellFromHomeOrderCancelledDrawer from './SellFromHomeOrderCancelledDrawer';
import {
  NOT_AVAILABLE,
  FIELD_CAPTION_TYPE,
  getTransactionField,
  buildTimelineSteps,
} from './sellFromHomeTransactionHelpers';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import { selectPortfolioSingleTransaction } from '../../../store/selectors/portfolioTransactionsSelector';
import {
  selectSellFromHomeHistory,
  selectSellFromHomeHistoryError,
} from '../../../store/selectors/sellFromHomeHistorySelectors';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import {
  portfolioTransactionsSingleFetch,
  emptyPortfolioTransactionSingle,
  accountTransactionsFetch,
} from '../../../store/actions/portfolio/portfolioActions';
import { RECENT_TRANSACTIONS } from '../../../constants/accountTransactionConstants';
import {
  getSellFromHomeHistory,
  emptySellFromHomeHistory,
  cancelSellFromHomeOrder,
} from '../../../store/actions/orders/orderActions';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { GET_SELL_FROM_HOME_HISTORY_LOADING } from '../../../store/actions/orders/orderActionConstants';
import {
  formatDate,
  formatSfhTimelineDate,
} from '../../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';

const SkuSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding: 8px 0 16px;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const SkuImage = styled.div`
  flex-shrink: 0;

  > img {
    width: 86px;
    height: 86px;
    object-fit: contain;
    margin: 0;

    ${mediaBelow(variables.breakpoints.bpMd)} {
      width: 50px;
      height: 50px;
    }
  }
`;

const SkuText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SkuName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-family: ${variables.family.goldSilverHeaders}, sans-serif;
    font-size: 14px;
    color: ${themeColors.colorSfhTextPrimary};
  }
`;

const SkuSub = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const TimelineTitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  color: ${themeColors.colorTextSecondary};
  flex-shrink: 0;
  padding: 16px 0 12px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const TimelineScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Footer = styled.div`
  > button {
    width: 100%;

    ${mediaBelow(variables.breakpoints.bpMd)} {
      height: 44px;
      font-family: ${variables.family.goldSilverHeaders}, sans-serif;
    }
  }
`;

const FetchErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorError};
  text-align: center;
  padding: 24px 0;
`;

const TimelineNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 4px;
  padding: 12px;
  margin-top: 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  > span {
    color: ${themeColors.colorTextSecondary};
  }
`;

const SellFromHomeTransactionHistory = ({
  selectedHit,
  quantity,
  lockedSnapshot,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const history = useSelector(selectSellFromHomeHistory);
  const historyError = useSelector(selectSellFromHomeHistoryError);
  const isFetching = useSelector(
    selectIsLoadingByActionTypes([
      PORTFOLIO_TRANSACTIONS_SINGLE_LOADING,
      GET_SELL_FROM_HOME_HISTORY_LOADING,
    ]),
  );
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isCancelledOpen, setIsCancelledOpen] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledAt, setCancelledAt] = useState(null);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const transactionUid = lockedSnapshot?.transactionUid;

  useEffect(() => {
    if (!accountUid || !transactionUid) return undefined;
    dispatch(portfolioTransactionsSingleFetch({ accountUid, transactionUid }));
    return () => {
      dispatch(emptyPortfolioTransactionSingle());
    };
  }, [accountUid, transactionUid, dispatch]);

  const fields = singleTransaction?.Fields ?? [];

  useEffect(() => {
    if (!accountUid || !transactionUid) return undefined;
    dispatch(getSellFromHomeHistory({ accountUid, transactionUid }));
    return () => {
      dispatch(emptySellFromHomeHistory());
    };
  }, [accountUid, transactionUid, dispatch]);

  // When launched from the wizard we have the selected product (selectedHit);
  // when launched from Recent Transactions we only have a Uid, so fall back to
  // the product details carried in the fetched transaction fields.
  const productImageField = fields.find((f) => f.FormatType === 'ProductImage');
  const productField = fields.find((f) => f.FormatType === 'Product');
  const summaryField = fields.find((f) => f.CaptionType === 'Summary');

  const productCode = selectedHit?.Code ?? productImageField?.ProductCode;
  const productCaption = selectedHit
    ? `${selectedHit?.WeightCaption ?? ''} ${selectedHit?.Caption ?? ''}`.trim()
    : productField?.ProductCaption ?? '';
  const displayQuantity = quantity || summaryField?.AmountOrQuantity;
  // Show the backend timestamp exactly as received (no timezone conversion);
  // only fall back to the client submission time if the BE value is missing.
  const submittedAt =
    singleTransaction?.DateTime || lockedSnapshot?.submittedAt;

  // Prefer the in-session locked payout; when opened from Recent Transactions
  // there's no snapshot, so fall back to the fetched Order Total field.
  const orderTotalField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.ORDER_TOTAL,
  );
  let subtitleAmount = NOT_AVAILABLE;
  if (lockedSnapshot?.payout != null) {
    subtitleAmount = formatMoneyNumeral(lockedSnapshot.payout);
  } else if (orderTotalField?.Amount != null) {
    subtitleAmount = formatMoneyNumeral(orderTotalField.Amount);
  }
  const cancelSubtitle = `${displayQuantity} × ${productCaption} · ${subtitleAmount}`;

  const cancelledNotice = (
    <TimelineNotice>
      <AmberAlertTriangle />
      <span>
        {t('sellWizard.sfhWizard.transactionHistory.marketLossNotice')}
      </span>
    </TimelineNotice>
  );

  const builtSteps = buildTimelineSteps(history);
  // The order is cancelled if we just cancelled it in-session (isCancelled) or
  // the fetched history already carries a terminal Cancelled step (reopened
  // from Recent Transactions). Either way the market-loss notice is shown.
  const orderCancelled =
    isCancelled || builtSteps.some((step) => step.status === 'cancelled');

  // When cancelled, the timeline ends on the terminal Cancelled status with the
  // cancellation time; the market-loss notice is rendered full-width below the
  // timeline. Otherwise show the progress sequence with the current status.
  const steps = isCancelled
    ? [
        {
          key: 'requestSubmitted',
          label: t(
            'sellWizard.sfhWizard.transactionHistory.status_requestSubmitted',
          ),
          completed: true,
          subText: submittedAt ? formatSfhTimelineDate(submittedAt) : null,
        },
        {
          key: 'cancelled',
          label: t('sellWizard.sfhWizard.transactionHistory.status_cancelled'),
          cancelled: true,
          subText: cancelledAt ? formatSfhTimelineDate(cancelledAt) : null,
        },
      ]
    : builtSteps.map((step, idx) => {
        const completed = step.status === 'completed';
        const isCancelledStep = step.status === 'cancelled';
        let subText = null;
        if (step.dateTime) {
          subText = formatSfhTimelineDate(step.dateTime);
        } else if (idx === 0 && completed && submittedAt) {
          // Fall back to the client submission time if the BE omits the
          // timestamp on the first (Request Submitted) step.
          subText = formatSfhTimelineDate(submittedAt);
        } else if (!completed && !isCancelledStep) {
          subText = t('sellWizard.sfhWizard.transactionHistory.pending');
        }
        return {
          key: step.key,
          label: t(
            `sellWizard.sfhWizard.transactionHistory.status_${step.key}`,
          ),
          completed,
          cancelled: isCancelledStep,
          subText,
          // Cancel Order is only available while the BE still flags the step
          // as cancellable (the first step before it leaves Request Submitted).
          showCancel: Boolean(step.cancellable),
        };
      });

  const handleCancelOrder = () => {
    setCancelError('');
    setIsCancelOpen(true);
  };

  // Submit the cancellation to POST
  // /accounts/{accountUid}/transactions/{transactionUid}/sfh/cancel.
  // On success, close the reason drawer and surface the cancelled confirmation
  // + terminal Cancelled timeline; on failure, keep the drawer open with an
  // inline error so the user can retry.
  const handleCancelConfirmed = (reason) => {
    if (!accountUid || !transactionUid || isConfirmingCancel) return;
    setCancelError('');
    setIsConfirmingCancel(true);
    dispatch(
      cancelSellFromHomeOrder({
        accountUid,
        transactionUid,
        CancellationReason: reason,
        isLoading: setIsConfirmingCancel,
        onSuccess: () => {
          setIsConfirmingCancel(false);
          setIsCancelOpen(false);
          setIsCancelled(true);
          dispatch(
            accountTransactionsFetch({
              accountUid,
              content: RECENT_TRANSACTIONS,
            }),
          );
          setCancelledAt(new Date());
          setIsCancelledOpen(true);
        },
        onError: (message) => {
          setIsConfirmingCancel(false);
          setCancelError(
            typeof message === 'string' && message
              ? message
              : t('sellWizard.sfhWizard.cancelDrawer.cancelFailed'),
          );
        },
      }),
    );
  };

  const handleCancelDrawerClose = () => {
    if (isConfirmingCancel) return;
    setCancelError('');
    setIsCancelOpen(false);
  };

  const handleCancelledOk = () => setIsCancelledOpen(false);

  const hasHistory = Array.isArray(history) && history.length > 0;
  const showLoader =
    !hasHistory &&
    !historyError &&
    (isFetching || Boolean(accountUid && transactionUid));

  return (
    <SectionLoader isLoading={showLoader}>
      <ModalBody>
        <SkuSection>
          <SkuImage>
            <ProductImage productCode={productCode} width={86} height={86} />
          </SkuImage>
          <SkuText>
            <SkuName>
              {t('sellWizard.sfhWizard.transactionHistory.productLine', {
                qty: displayQuantity,
                caption: productCaption,
              })}
            </SkuName>
            <SkuSub>
              {t('sellWizard.sfhWizard.transactionHistory.soldFrom', {
                date: submittedAt ? formatDate(submittedAt) : '',
              })}
            </SkuSub>
          </SkuText>
        </SkuSection>

        <TimelineTitle>
          {t('sellWizard.sfhWizard.transactionHistory.timeline')}
        </TimelineTitle>

        <TimelineScroll>
          {historyError && !hasHistory && !isCancelled ? (
            <FetchErrorText data-cy="text-sfh-history-fetch-error">
              {t('sellWizard.sfhWizard.transactionHistory.fetchFailed')}
            </FetchErrorText>
          ) : (
            <>
              <SellFromHomeStatusTimeline
                steps={steps}
                cancelLabel={t(
                  'sellWizard.sfhWizard.transactionHistory.cancelOrder',
                )}
                onCancelOrder={handleCancelOrder}
              />
              {orderCancelled && cancelledNotice}
            </>
          )}
        </TimelineScroll>

        <Footer>
          <SfhSecondaryButton
            onClick={handleClose}
            data-cy="button-sfh-history-close"
          >
            {t('sellWizard.sfhWizard.transactionHistory.close')}
          </SfhSecondaryButton>
        </Footer>
      </ModalBody>

      <SellFromHomeCancelOrderDrawer
        isOpen={isCancelOpen}
        onClose={handleCancelDrawerClose}
        onConfirm={handleCancelConfirmed}
        subtitle={cancelSubtitle}
        isConfirming={isConfirmingCancel}
        errorMessage={cancelError}
      />

      <SellFromHomeOrderCancelledDrawer
        isOpen={isCancelledOpen}
        onClose={handleCancelledOk}
        subtitle={cancelSubtitle}
      />
    </SectionLoader>
  );
};

SellFromHomeTransactionHistory.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedSnapshot: PropTypes.shape({
    transactionUid: PropTypes.string,
    submittedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    payout: PropTypes.number,
  }),
  handleClose: PropTypes.func,
};

export default SellFromHomeTransactionHistory;
